import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { CheckboxField, SelectField, TextField } from "@/components/ui/Field";
import { IconButton } from "@/components/ui/IconButton";
import type { AdminQueue, ScopeRule } from "@/lib/admin-api";

const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : "");
const fromDateInput = (value: string) => (value ? `${value}T00:00:00Z` : null);

interface Props {
  rules: ScopeRule[];
  queues: AdminQueue[];
  onChange: (rules: ScopeRule[]) => void;
  emptyText?: string;
}

/** Edits a list of scope rules: queue (optionally with sub-queues), customer filter and period. */
export function ScopeRulesEditor({ rules, queues, onChange, emptyText }: Props) {
  const { t } = useTranslation();
  const update = (index: number, patch: Partial<ScopeRule>) =>
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  const add = () =>
    onChange([
      ...rules,
      { queue_id: null, include_subqueues: true, customer: null, date_from: null, date_to: null },
    ]);

  return (
    <div className="space-y-3">
      {rules.length === 0 && (
        <p className="text-sm text-ink-subtle">{emptyText ?? t("admin.scope.noRules")}</p>
      )}
      {rules.map((rule, index) => (
        <fieldset key={index} className="space-y-3 rounded-control border border-line p-4">
          <div className="flex items-center justify-between">
            <legend className="text-sm font-medium">{t("admin.scope.rule", { n: index + 1 })}</legend>
            <IconButton
              label={t("admin.scope.removeRule")}
              onClick={() => onChange(rules.filter((_, i) => i !== index))}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </IconButton>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label={t("admin.scope.queue")}
              value={rule.queue_id ?? ""}
              onChange={(e) => update(index, { queue_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">{t("admin.scope.allQueues")}</option>
              {queues.map((q) => (
                <option key={q.id} value={q.id}>
                  {"  ".repeat(q.depth)}
                  {q.name}
                  {q.is_valid ? "" : ` (${t("admin.scope.retired")})`}
                </option>
              ))}
            </SelectField>
            <TextField
              label={t("admin.scope.customer")}
              hint={t("admin.scope.customerHint")}
              value={rule.customer ?? ""}
              onChange={(e) => update(index, { customer: e.target.value || null })}
            />
            <TextField
              label={t("admin.scope.from")}
              type="date"
              value={toDateInput(rule.date_from)}
              onChange={(e) => update(index, { date_from: fromDateInput(e.target.value) })}
            />
            <TextField
              label={t("admin.scope.to")}
              type="date"
              value={toDateInput(rule.date_to)}
              onChange={(e) => update(index, { date_to: fromDateInput(e.target.value) })}
            />
          </div>
          <CheckboxField
            label={t("admin.scope.includeSubqueues")}
            checked={rule.include_subqueues}
            disabled={rule.queue_id === null}
            onChange={(e) => update(index, { include_subqueues: e.target.checked })}
          />
        </fieldset>
      ))}
      <Button variant="secondary" onClick={add}>
        <Plus className="size-4" aria-hidden="true" />
        {t("admin.scope.addRule")}
      </Button>
    </div>
  );
}
